// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { AnyAction, Dispatch, ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ReferencesQuery, StorageLocation, CombinedState } from 'reducers';
import {
    getCore, RQStatus, Storage, Reference,
} from 'cvat-core-wrapper';
import { filterNull } from 'utils/filter-null';
import { getCVATStore } from 'cvat-store';
import Notification from 'antd/lib/notification';
import { getInferenceStatusAsync } from './models-actions';

const cvat = getCore();

export enum ReferencesActionTypes {
    GET_REFERENCES = 'GET_REFERENCES',
    GET_REFERENCES_SUCCESS = 'GET_REFERENCES_SUCCESS',
    GET_REFERENCES_FAILED = 'GET_REFERENCES_FAILED',
    DELETE_REFERENCE = 'DELETE_REFERENCE',
    DELETE_REFERENCE_SUCCESS = 'DELETE_REFERENCE_SUCCESS',
    DELETE_REFERENCE_FAILED = 'DELETE_REFERENCE_FAILED',
    CREATE_REFERENCE_FAILED = 'CREATE_REFERENCE_FAILED',
    SWITCH_MOVE_REFERENCE_MODAL_VISIBLE = 'SWITCH_MOVE_REFERENCE_MODAL_VISIBLE',
    GET_REFERENCE_PREVIEW = 'GET_REFERENCE_PREVIEW',
    GET_REFERENCE_PREVIEW_SUCCESS = 'GET_REFERENCE_PREVIEW_SUCCESS',
    GET_REFERENCE_PREVIEW_FAILED = 'GET_REFERENCE_PREVIEW_FAILED',
    UPDATE_REFERENCE_IN_STATE = 'UPDATE_REFERENCE_IN_STATE',
    MODEL_REFERENCE = 'MODEL_REFERENCE',
    MODEL_REFERENCE_SUCCESS = 'MODEL_REFERENCE_SUCCESS',
    MODEL_REFERENCE_FAILED = 'MODEL_REFERENCE_FAILED',
    LINK_TASK_TO_REFERENCE = "LINK_TASK_TO_REFERENCE",
}

function getReferences(query: Partial<ReferencesQuery>, updateQuery: boolean): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCES,
        payload: {
            updateQuery,
            query,
        },
    };

    return action;
}

export function getReferencesSuccess(array: any[], count: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCES_SUCCESS,
        payload: {
            array,
            count,
        },
    };

    return action;
}

function getReferencesFailed(error: any): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCES_FAILED,
        payload: { error },
    };

    return action;
}

export function getReferencesAsync(
    query: Partial<ReferencesQuery>,
    updateQuery = true,
): ThunkAction<Promise<void>, {}, {}, AnyAction> {
    return async (dispatch: ActionCreator<Dispatch>): Promise<void> => {
        dispatch(getReferences(query, updateQuery));

        const filteredQuery = filterNull(query);

        let result = null;
        try {
            result = await cvat.references.get(filteredQuery);
        } catch (error) {
            dispatch(getReferencesFailed(error));
            return;
        }

        const array = Array.from(result);

        dispatch(getInferenceStatusAsync());
        dispatch(getReferencesSuccess(array, result.count));
    };
}

function deleteReference(referenceID: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.DELETE_REFERENCE,
        payload: {
            referenceID,
        },
    };

    return action;
}

function deleteReferenceSuccess(referenceID: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.DELETE_REFERENCE_SUCCESS,
        payload: {
            referenceID,
        },
    };

    return action;
}

function deleteReferenceFailed(referenceID: number, error: any): AnyAction {
    const action = {
        type: ReferencesActionTypes.DELETE_REFERENCE_FAILED,
        payload: {
            referenceID,
            error,
        },
    };

    return action;
}

export function deleteReferenceAsync(referenceInstance: any): ThunkAction<Promise<void>, {}, {}, AnyAction> {
    return async (dispatch: ActionCreator<Dispatch>): Promise<void> => {
        try {
            dispatch(deleteReference(referenceInstance.id));
            await referenceInstance.delete();
        } catch (error) {
            dispatch(deleteReferenceFailed(referenceInstance.id, error));
            return;
        }

        dispatch(deleteReferenceSuccess(referenceInstance.id));
    };
}

function createReferenceFailed(error: any): AnyAction {
    const action = {
        type: ReferencesActionTypes.CREATE_REFERENCE_FAILED,
        payload: {
            error,
        },
    };

    return action;
}

function getReferencePreview(referenceID: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCE_PREVIEW,
        payload: {
            referenceID,
        },
    };

    return action;
}

function getReferencePreviewSuccess(referenceID: number, preview: string): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCE_PREVIEW_SUCCESS,
        payload: {
            referenceID,
            preview,
        },
    };

    return action;
}

function getReferencePreviewFailed(referenceID: number, error: any): AnyAction {
    const action = {
        type: ReferencesActionTypes.GET_REFERENCE_PREVIEW_FAILED,
        payload: {
            referenceID,
            error,
        },
    };

    return action;
}

export function getReferencePreviewAsync(referenceInstance: any): ThunkAction<Promise<void>, {}, {}, AnyAction> {
    return async (dispatch: ActionCreator<Dispatch>): Promise<void> => {
        try {
            dispatch(getReferencePreview(referenceInstance.id));
            const result = await referenceInstance.frames.preview();
            dispatch(getReferencePreviewSuccess(referenceInstance.id, result));
        } catch (error) {
            dispatch(getReferencePreviewFailed(referenceInstance.id, error));
        }
    };
}

export function updateReferenceInState(reference: Reference): AnyAction {
    const action = {
        type: ReferencesActionTypes.UPDATE_REFERENCE_IN_STATE,
        payload: { reference },
    };

    return action;
}

export function createReferenceAsync(
    data: any,
    onProgress?: (status: string) => void,
): ThunkAction<Promise<void>, {}, {}, AnyAction> {
    return async (dispatch): Promise<any> => {
        const description: any = {
            name: data.basic.name,
            labels: data.labels,
            image_quality: 100,
            use_zip_chunks: data.advanced.useZipChunks,
            use_cache: data.advanced.useCache,
            sorting_method: data.advanced.sortingMethod,
            source_storage: new Storage(data.advanced.sourceStorage || { location: StorageLocation.LOCAL }).toJSON(),
            target_storage: new Storage(data.advanced.targetStorage || { location: StorageLocation.LOCAL }).toJSON(),
        };

        if (data.projectId) {
            description.project_id = data.projectId;
        }
        if (data.taskId) {
            description.tasks = data.taskId;
        }
        if (data.advanced.bugTracker) {
            description.bug_tracker = data.advanced.bugTracker;
        }
        if (data.advanced.segmentSize) {
            description.segment_size = +data.advanced.segmentSize;
        }
        if (data.advanced.overlapSize) {
            description.overlap = data.advanced.overlapSize;
        }
        if (data.advanced.startFrame) {
            description.start_frame = +data.advanced.startFrame;
        }
        if (data.advanced.stopFrame) {
            description.stop_frame = +data.advanced.stopFrame;
        }
        if (data.advanced.frameFilter) {
            description.frame_filter = data.advanced.frameFilter;
        }
        if (data.advanced.imageQuality) {
            description.image_quality = +data.advanced.imageQuality;
        }
        if (data.advanced.dataChunkSize) {
            description.data_chunk_size = +data.advanced.dataChunkSize;
        }
        if (data.advanced.copyData) {
            description.copy_data = data.advanced.copyData;
        }
        if (data.subset) {
            description.subset = data.subset;
        }
        if (data.cloudStorageId) {
            description.cloud_storage_id = data.cloudStorageId;
        }

        const referenceInstance = new cvat.classes.Reference(description);
        (referenceInstance as any).clientFiles = data.files.local;
        (referenceInstance as any).serverFiles = data.files.share.concat(data.files.cloudStorage);
        (referenceInstance as any).remoteFiles = data.files.remote;

        try {
            const savedReference = await referenceInstance.save(((
                status: RQStatus,
                progress: number,
                message: string,
            ): void => {
                if (status === RQStatus.UNKNOWN) {
                    onProgress?.(`${message} ${progress ? `${Math.floor(progress * 100)}%` : ''}`);
                } else if ([RQStatus.QUEUED, RQStatus.STARTED].includes(status)) {
                    const helperMessage = 'You may close the window.';
                    onProgress?.(`${message} ${progress ? `${Math.floor(progress * 100)}%` : ''}. ${helperMessage}`);
                } else {
                    onProgress?.(`${status}: ${message}`);
                }
            }) as any);
            dispatch(updateReferenceInState(savedReference));
            dispatch(getReferencePreviewAsync(savedReference));
            return savedReference;
        } catch (error) {
            dispatch(createReferenceFailed(error));
            throw error;
        }
    };
}

export function switchMoveReferenceModalVisible(visible: boolean, referenceId: number | null = null): AnyAction {
    const action = {
        type: ReferencesActionTypes.SWITCH_MOVE_REFERENCE_MODAL_VISIBLE,
        payload: {
            referenceId,
            visible,
        },
    };

    return action;
}

function modelReference(referenceID: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.MODEL_REFERENCE,
        payload: {
            referenceID,
        },
    };

    return action;
}

function modelReferenceSuccess(referenceID: number): AnyAction {
    const action = {
        type: ReferencesActionTypes.MODEL_REFERENCE_SUCCESS,
        payload: {
            referenceID,
        },
    };

    return action;
}

function modelReferenceFailed(referenceID: number, error: any): AnyAction {
    const action = {
        type: ReferencesActionTypes.MODEL_REFERENCE_FAILED,
        payload: {
            referenceID,
            error,
        },
    };

    return action;
}

export function linkTaskToReference(referenceID: number, taskIds: number[]): AnyAction {
    const action = {
        type: ReferencesActionTypes.LINK_TASK_TO_REFERENCE,
        payload: { referenceID, taskIds },
    };

    return action;
}

export const SET_LOADING = 'SET_LOADING';
export const UNSET_LOADING = 'UNSET_LOADING';

export const setLoading = (key: string) => ({
    type: SET_LOADING,
    payload: key,
});

export const unsetLoading = (key: string) => ({
    type: UNSET_LOADING,
    payload: key,
});

export function modelReferenceAsync(referenceInstance: any): ThunkAction<Promise<void>, {}, {}, AnyAction> {
    return async (dispatch: ActionCreator<Dispatch>): Promise<void> => {
        const store = getCVATStore();
        const state: CombinedState = store.getState();
        try {
            const notificationKey = `modelSearchNotification${referenceInstance.id}`;
            Notification.info({
                message: 'Model search started',
                description:
                    'Model search was started. ' + 'Models will show automatically as soon as the search is finished.',
                className: 'cvat-notification-notice-export-backup-start',
                key: notificationKey,
                duration: null,
            });
            dispatch(modelReference(referenceInstance.id));
            await referenceInstance.model();
            Notification.close(notificationKey);
        } catch (error) {
            dispatch(modelReferenceFailed(referenceInstance.id, error));
            return;
        }

        dispatch(modelReferenceSuccess(referenceInstance.id));
    };
}
